import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useContent } from '../hooks/useContent';

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage = 20,
  totalItems = 0,
  currentItemsCount = 0,
  loading = false
}) => {
  const { content } = useContent('pagination');

  const t = content || {
    empty: 'Aucun QR code à afficher',
    showing: 'Affichage de',
    to: 'à',
    of: 'sur',
    previous: 'Précédent',
    next: 'Suivant',
  };

  const startItem = currentItemsCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = currentItemsCount === 0 ? 0 : Math.min(
    startItem + currentItemsCount - 1,
    totalItems > 0 ? totalItems : Infinity
  );

  const handlePrevious = () => {
    if (currentPage > 1) onPageChange(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) onPageChange(currentPage + 1);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) pages.push('...');
      }
      for (let i = startPage; i <= endPage; i++) pages.push(i);
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  if (totalPages <= 1) return null;

  const countText = currentItemsCount === 0
    ? t.empty
    : `${t.showing} ${startItem} ${t.to} ${endItem}${totalItems > 0 ? ` ${t.of} ${totalItems}` : ''}`;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 bg-gray-800 border-t border-gray-700">
      <div className="flex items-center text-sm text-gray-400 mb-4 sm:mb-0">
        <span>{countText}</span>
      </div>

      <div className="flex items-center space-x-2">
        <button
          onClick={handlePrevious}
          disabled={currentPage === 1 || loading}
          className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
            currentPage === 1 || loading
              ? 'text-gray-500 cursor-not-allowed bg-gray-800'
              : 'text-gray-300 hover:text-white hover:bg-gray-700 bg-gray-800'
          }`}
        >
          <ChevronLeftIcon className="w-4 h-4 mr-1" />
          <span className="hidden sm:inline">{t.previous}</span>
        </button>

        <div className="flex items-center space-x-1">
          {getPageNumbers().map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span className="px-3 py-2 text-gray-500">...</span>
              ) : (
                <button
                  onClick={() => onPageChange(page)}
                  disabled={loading}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    page === currentPage
                      ? 'bg-blue-600 text-white'
                      : loading
                      ? 'text-gray-500 cursor-not-allowed'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        <button
          onClick={handleNext}
          disabled={currentPage === totalPages || loading}
          className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
            currentPage === totalPages || loading
              ? 'text-gray-500 cursor-not-allowed bg-gray-800'
              : 'text-gray-300 hover:text-white hover:bg-gray-700 bg-gray-800'
          }`}
        >
          <span className="hidden sm:inline">{t.next}</span>
          <ChevronRightIcon className="w-4 h-4 ml-1" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
